<template>
  <div class="page-outer">
    <div class="page-header page-header-fixed-form">
      <div class="header-left">
        <h1 class="page-title">{{ pageTitle }}</h1>
      </div>
      <div class="header-actions">
        <button type="button" class="btn btn-secondary" @click="handleReset" :disabled="submitting">
          重置
        </button>
        <button type="button" class="btn btn-primary" :disabled="submitting" @click="handleSubmit">
          <span v-if="submitting">
            <i class="ri-loader-4-line spin"></i>
            處理中...
          </span>
          <span v-else>
            <i class="ri-check-line"></i>
            確認調整
          </span>
        </button>
      </div>
    </div>

    <div class="page-container">

    <div v-if="isWixtarClient" class="alert alert-info wixtar-banner" role="status">
      <strong>Wixtar 客戶：</strong>調整將透過 Wixtar API 寫入（以 Wixtar 為準），CRM 顯示餘額與流水為同步後副本。
    </div>

    <div class="form-container">
      <form @submit.prevent="handleSubmit">
          <div class="form-section">
            <h2 class="section-title">調整資訊</h2>
            
            <div class="form-group">
              <MemberSearch
                ref="memberSearchRef"
                v-model="selectedMembers"
                :max-selections="1"
                :disabled="submitting"
                placeholder="輸入手機號碼或姓名搜尋會員"
                @selected="handleMemberSelected"
                @cleared="handleMemberCleared"
              />
              <span v-if="errors.memberId" class="error-text">{{ errors.memberId }}</span>
            </div>

            <div class="form-group">
              <label class="form-label">點數 <span class="required">*</span></label>
              <input
                v-model.number="form.points"
                type="number"
                class="form-control"
                placeholder="正數為增加，負數為扣除"
                required
              />
              <span v-if="errors.points" class="error-text">{{ errors.points }}</span>
              <small class="form-text">輸入正數增加點數，輸入負數扣除點數</small>
            </div>

            <div class="form-group">
              <label class="form-label">調整原因 <span class="required">*</span></label>
              <textarea
                v-model="form.reason"
                class="form-control"
                rows="3"
                placeholder="請說明調整原因"
                required
              ></textarea>
              <span v-if="errors.reason" class="error-text">{{ errors.reason }}</span>
            </div>

            <div class="form-group">
              <label class="form-label">過期日期（選填）</label>
              <input
                v-model="form.expiryDate"
                type="date"
                class="form-control"
              />
              <small class="form-text">留空則使用系統預設過期規則</small>
            </div>
          </div>

        </form>
    </div>
    </div>
  </div>
</template>

<script>
import { ref, reactive, onMounted } from 'vue';
import { usePageTitle } from '@/composables/usePageTitle.js';
import { pointsService } from '@/api/modules/points.js';
import { clientService } from '@/api/modules/clients.js';
import authService from '@/helpers/authservice/authService.js';
import { showSuccess, showError } from '@/utils/swal.js';
import MemberSearch from '@/components/MemberSearch.vue';

export default {
  name: 'PointsAdjust',
  components: {
    MemberSearch
  },
  setup() {
    const { pageTitle } = usePageTitle('手動調整點數');
    const submitting = ref(false);
    const memberInfo = ref(null);
    const memberSearchRef = ref(null);
    const selectedMembers = ref([]);

    const form = reactive({
      memberId: '',
      points: null,
      reason: '',
      expiryDate: ''
    });

    const errors = reactive({});
    const isWixtarClient = ref(false);

    const loadClientMemberSystem = async () => {
      const user = authService.getCurrentUser();
      if (!user?.clientId) return;
      try {
        const res = await clientService.getClient(user.clientId);
        if (res?.success && res.data?.clientData?.memberSystemType) {
          isWixtarClient.value = res.data.clientData.memberSystemType === 'WIXTAR';
        }
      } catch (e) {
        console.error(e);
      }
    };

    onMounted(() => {
      loadClientMemberSystem();
    });

    const handleMemberSelected = (member) => {
      if (member) {
        memberInfo.value = member;
        form.memberId = member.id;
        errors.memberId = '';
      } else {
        memberInfo.value = null;
        form.memberId = '';
      }
    };

    const handleMemberCleared = () => {
      selectedMembers.value = [];
      memberInfo.value = null;
      form.memberId = '';
      errors.memberId = '';
    };

    const validateForm = () => {
      errors.memberId = '';
      errors.points = '';
      errors.reason = '';

      if (!form.memberId || !memberInfo.value) {
        errors.memberId = '請先搜尋並選擇會員';
        return false;
      }

      if (form.points === null || form.points === 0) {
        errors.points = '點數不能為 0';
        return false;
      }

      if (!form.reason || form.reason.trim().length === 0) {
        errors.reason = '請輸入調整原因';
        return false;
      }

      return true;
    };

    const handleSubmit = async () => {
      if (!validateForm()) {
        return;
      }

      submitting.value = true;
      try {
        const data = {
          memberId: form.memberId,
          points: form.points > 0 ? form.points : Math.abs(form.points),
          reason: form.reason
        };
        
        // 只有當 expiryDate 有值時才加入請求
        if (form.expiryDate && form.expiryDate.trim()) {
          data.expiryDate = form.expiryDate;
        }

        const response = form.points > 0
          ? await pointsService.addPoints(data)
          : await pointsService.deductPoints(data);

        if (response.success) {
          await showSuccess('點數調整成功', `已${form.points > 0 ? '增加' : '扣除'} ${Math.abs(form.points)} 點`);
          handleReset();
        }
      } catch (error) {
        showError('點數調整失敗', error.message || '請檢查輸入資料');
      } finally {
        submitting.value = false;
      }
    };

    const handleReset = () => {
      selectedMembers.value = [];
      memberSearchRef.value?.reset();
      form.memberId = '';
      form.points = null;
      form.reason = '';
      form.expiryDate = '';
      memberInfo.value = null;
      Object.keys(errors).forEach(key => {
        errors[key] = '';
      });
    };

    return {
      pageTitle,
      form,
      errors,
      submitting,
      memberInfo,
      memberSearchRef,
      selectedMembers,
      handleMemberSelected,
      handleMemberCleared,
      handleSubmit,
      handleReset,
      isWixtarClient
    };
  }
};
</script>

<style scoped>
.wixtar-banner {
  margin: 0 0 16px;
  padding: 12px 16px;
  border-radius: 8px;
  font-size: 14px;
}

.form-section {
  margin-bottom: 30px;
}

.section-title {
  font-size: 18px;
  font-weight: 600;
  margin-bottom: 20px;
  padding-bottom: 10px;
  border-bottom: 1px solid #e0e0e0;
}

.form-group {
  margin-bottom: 20px;
}

.form-label {
  display: block;
  margin-bottom: 5px;
  font-weight: 500;
}

.required {
  color: #dc3545;
}

.error-text {
  display: block;
  color: #dc3545;
  font-size: 14px;
  margin-top: 5px;
}

.form-text {
  display: block;
  margin-top: 5px;
  color: #6c757d;
  font-size: 14px;
}

.form-actions {
  display: flex;
  gap: 10px;
  justify-content: flex-end;
  padding-top: 20px;
  border-top: 1px solid #e0e0e0;
}

.spin {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

</style>
